import SchoolIcon from "@mui/icons-material/School";
import {
  CheckCircle,
  Download,
  FileText,
  MapPin,
  Play,
  User,
  X,
} from "lucide-react";
import { useContext, useEffect, useState } from "react";
import DialogChatAPI from "../../api/dialogChatApi";
import WebinarApi from "../../api/webinarApi";
import Loader from "../../components/Loader/Loader";
import ModalWindow from "../../components/ModalWindow/ModalWindow";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { AvailableLangs, ISO639Lang } from "../../constans/languagesList";
import { ContextProvider } from "../../contextProvider";
import {
  TypeWebinarAgenda,
  TypeWebinarsFilters,
  Webinar,
} from "../../types/Webinar";
import { getTranslation } from "../../utils/getTranslation";
import Notify, { SuccesNotify } from "../../utils/toaster";
import { ReviewMessageModal } from "../ReviewSpecialists/components/ReviewMessageModal";
import s from "./Webinars.module.css";
import WebinarTeam from "./WebinarTeam/WebinarTeam";

interface ReviewStats {
  totalPending: number;
  reviewedToday: number;
  averageReviewTime: number;
  queuePosition: number;
}

interface WebinarFile {
  name: string;
  url: string;
  type: string;
}

interface FilePreviewState {
  isOpen: boolean;
  file: WebinarFile | null;
}

const Webinars = () => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);

  // Queue state
  const [reviewQueue, setReviewQueue] = useState<Webinar[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoader, setIsLoader] = useState(false);
  const [avatars, setAvatars] = useState<{ [webinarId: string]: string }>({});
  const [webinarFiles, setWebinarFiles] = useState<WebinarFile[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  // Stats state
  const [stats, setStats] = useState<ReviewStats>({
    totalPending: 0,
    reviewedToday: 0,
    averageReviewTime: 0,
    queuePosition: 0,
  });
  const [reviewTimes, setReviewTimes] = useState<number[]>([]);
  const [currentReviewStart, setCurrentReviewStart] = useState<number>(
    Date.now(),
  );

  // Modal state
  const [isApprovalMessageModalOpen, setIsApprovalMessageModalOpen] =
    useState(false);
  const [isRejectionMessageModalOpen, setIsRejectionMessageModalOpen] =
    useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<
    "approve" | "reject"
  >("approve");
  const [filePreview, setFilePreview] = useState<FilePreviewState>({
    isOpen: false,
    file: null,
  });

  const currentWebinar = reviewQueue[currentIndex];

  // Load review queue on mount
  useEffect(() => {
    loadReviewQueue();
  }, []);

  // Load avatars when queue changes
  useEffect(() => {
    const loadAvatars = async () => {
      if (token && reviewQueue.length > 0) {
        const webinarsToLoad = reviewQueue.slice(
          currentIndex,
          Math.min(currentIndex + 5, reviewQueue.length),
        );

        const promises = webinarsToLoad.map((webinar) =>
          WebinarApi.getAllWebinarFileSignedUrls(token, webinar._id),
        );
        const responses = await Promise.all(promises);
        const avatarsData: { [id: string]: string } = {};

        responses.forEach((response, idx) => {
          if (response.urls?.avatar) {
            avatarsData[webinarsToLoad[idx]._id] = response.urls.avatar;
          }
        });

        setAvatars((prev) => ({ ...prev, ...avatarsData }));
      }
    };
    loadAvatars();
  }, [reviewQueue, currentIndex, token]);

  // Update stats when queue or index changes
  useEffect(() => {
    setStats((prev) => ({
      ...prev,
      totalPending: reviewQueue.length - currentIndex,
      queuePosition: currentIndex + 1,
    }));
  }, [reviewQueue, currentIndex]);

  // Reset review start time when moving to next webinar
  useEffect(() => {
    setCurrentReviewStart(Date.now());
  }, [currentIndex]);

  // Load files for currently selected webinar
  useEffect(() => {
    const loadWebinarFiles = async () => {
      if (!token || !currentWebinar) {
        setWebinarFiles([]);
        return;
      }

      setIsLoadingFiles(true);
      try {
        const response = await WebinarApi.getAllWebinarFileSignedUrls(
          token,
          currentWebinar._id,
        );

        if (response.status && response.urls) {
          const files: WebinarFile[] = [];

          if (response.urls.avatar) {
            files.push({
              name: "Webinar Avatar",
              url: response.urls.avatar,
              type: "image",
            });
          }
          if (response.urls.video) {
            files.push({
              name: "Webinar Video",
              url: response.urls.video,
              type: "video",
            });
          }
          if (response.urls.pdfPresentation) {
            files.push({
              name: "PDF Presentation",
              url: response.urls.pdfPresentation,
              type: "pdf",
            });
          }
          if (response.urls.certificate) {
            files.push({
              name: "Certificate Template",
              url: response.urls.certificate,
              type: "certificate",
            });
          }
          if (response.urls.waitingScreenDesktopImage) {
            files.push({
              name: "Waiting Screen (Desktop)",
              url: response.urls.waitingScreenDesktopImage,
              type: "image",
            });
          }
          if (response.urls.waitingScreenMobileImage) {
            files.push({
              name: "Waiting Screen (Mobile)",
              url: response.urls.waitingScreenMobileImage,
              type: "image",
            });
          }

          setWebinarFiles(files);
        } else {
          setWebinarFiles([]);
        }
      } catch (error) {
        console.error("Error loading webinar files:", error);
        setWebinarFiles([]);
      } finally {
        setIsLoadingFiles(false);
      }
    };

    loadWebinarFiles();
  }, [token, currentWebinar]);

  const loadReviewQueue = async () => {
    if (!token) return;

    setIsLoader(true);
    try {
      const filters: TypeWebinarsFilters = {
        page: 1,
        limit: 100,
        isApproveRequested: true,
      };

      const response = await WebinarApi.getFilteredWebinars(token, filters);

      if (response.status && response.webinars) {
        setReviewQueue(response.webinars);
        setStats((prev) => ({
          ...prev,
          totalPending: response.webinars!.length,
        }));
      } else {
        setReviewQueue([]);
      }
    } catch (error) {
      console.error("Error loading review queue:", error);
      Notify("Failed to load review queue");
    } finally {
      setIsLoader(false);
    }
  };

  const recordReviewTime = () => {
    const reviewTime = Date.now() - currentReviewStart;
    const newReviewTimes = [...reviewTimes, reviewTime];
    setReviewTimes(newReviewTimes);

    const avgTime =
      newReviewTimes.reduce((a, b) => a + b, 0) / newReviewTimes.length;
    setStats((prev) => ({
      ...prev,
      averageReviewTime: Math.round(avgTime / 1000),
      reviewedToday: prev.reviewedToday + 1,
    }));
  };

  const handleApproveClick = () => {
    setConfirmationAction("approve");
    setIsConfirmationModalOpen(true);
  };

  const handleRejectClick = () => {
    setConfirmationAction("reject");
    setIsConfirmationModalOpen(true);
  };

  const handleApproveWithMessage = async (message: string) => {
    if (!token || !currentWebinar || !userData) return;

    try {
      // 1. Send admin message to specialist
      await sendAdminMessage(message);

      // 2. Update webinar status
      const response = await WebinarApi.update(token, {
        _id: currentWebinar._id,
        isApproved: true,
        isApproveRequested: false,
      });

      if (response.status) {
        recordReviewTime();
        setIsApprovalMessageModalOpen(false);
        SuccesNotify("Webinar approved and specialist notified");

        // Remove approved webinar from queue
        setReviewQueue((prev) => prev.filter((_, idx) => idx !== currentIndex));
      } else if (response.message) {
        Notify(response.message);
      }
    } catch (error) {
      console.error("Error approving webinar:", error);
      Notify("Failed to approve webinar");
    }
  };

  const handleRejectWithMessage = async (message: string) => {
    if (!token || !currentWebinar || !userData) return;

    try {
      // 1. Send admin message to specialist
      await sendAdminMessage(message);

      // 2. Update webinar status
      const response = await WebinarApi.update(token, {
        _id: currentWebinar._id,
        isApproved: false,
        isApproveRequested: false,
      });

      if (response.status) {
        recordReviewTime();
        setIsRejectionMessageModalOpen(false);
        SuccesNotify("Webinar rejected and specialist notified with feedback");

        // Remove rejected webinar from queue
        setReviewQueue((prev) => prev.filter((_, idx) => idx !== currentIndex));
      } else if (response.message) {
        Notify(response.message);
      }
    } catch (error) {
      console.error("Error rejecting webinar:", error);
      Notify("Failed to reject webinar");
    }
  };

  const sendAdminMessage = async (message: string) => {
    if (!token || !userData || !currentWebinar || !currentWebinar.createdBy)
      return;

    // Create or get dialog chat with the specialist
    const chatResponse = await DialogChatAPI.createDialogChat(token, {
      userId: userData._id,
      userIdToSend: currentWebinar.createdBy._id,
    });

    if (!chatResponse.status) {
      throw new Error("Failed to create chat");
    }

    // Send the message via socket
    await DialogChatAPI.sendMessage({
      userIdToSend: currentWebinar.createdBy._id,
      message: message,
      userId: userData._id,
      date: new Date(),
      files: [],
    });
  };

  const moveToNext = () => {
    if (currentIndex < reviewQueue.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      loadReviewQueue();
      setCurrentIndex(0);
    }
  };

  const moveToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid date";
    }
  };

  const getSpecialistName = (): string => {
    if (!currentWebinar?.createdBy) return "Specialist";
    const firstName = Array.isArray(currentWebinar.createdBy.name)
      ? currentWebinar.createdBy.name[0]?.text
      : currentWebinar.createdBy.name || "";
    const lastName = Array.isArray(currentWebinar.createdBy.surname)
      ? currentWebinar.createdBy.surname[0]?.text
      : currentWebinar.createdBy.surname || "";
    return `${firstName} ${lastName}`.trim();
  };

  const handleFileClick = (file: WebinarFile) => {
    setFilePreview({ isOpen: true, file });
  };

  const closeFilePreview = () => {
    setFilePreview({ isOpen: false, file: null });
  };

  const webinarApprovalTemplates: {
    [key in AvailableLangs]?: (name: string, spec?: string) => string;
  } = {
    en: (name: string) => `Dear ${name},

Congratulations! Your webinar "${currentWebinar.title}" has been reviewed and approved.

Activate your webinar to make it available to users on the platform.

Thank you for creating valuable content and being part of our platform!

Best regards,
Review Team`,

    ru: (name: string) => `Уважаемый(ая) ${name},

Поздравляем! Ваш вебинар «${currentWebinar.title}» был проверен и одобрен.

Активируйте свой вебинар, чтобы он был доступен пользователям платформы.

Спасибо за ваш вклад и качественный контент!

С уважением,
Команда модерации`,

    uk: (name: string) => `Шановний(а) ${name},

Вітаємо! Ваш вебінар «${currentWebinar.title}» було перевірено та схвалено.

Активуйте свій вебінар, щоб він був доступний користувачам платформи.

Дякуємо за ваш внесок і корисний контент!

З повагою,
Команда модерації`,
  };

  const webinarRejectionTemplates: {
    [key in AvailableLangs]?: (
      name: string,
      spec?: string,
      fields?: string,
    ) => string;
  } = {
    en: (name: string, reasons?: string) => {
      let msg = `Dear ${name},

Thank you for submitting your webinar "${currentWebinar.title}" for review.

Unfortunately, we cannot approve this webinar at the moment. Please review the feedback below and make the necessary changes before resubmitting:

`;
      if (reasons) {
        msg += reasons + "\n\n";
      } else {
        msg += "[Please specify the reasons for rejection]\n\n";
      }

      msg += `Once the issues are resolved, you may resubmit the webinar for review.

Best regards,
Review Team`;

      return msg;
    },

    ru: (name: string, reasons?: string) => {
      let msg = `Уважаемый(ая) ${name},

Спасибо за отправку вебинара «${currentWebinar.title}» на модерацию.

К сожалению, мы не можем одобрить данный вебинар в текущем виде. Пожалуйста, ознакомьтесь с замечаниями ниже и внесите необходимые изменения перед повторной отправкой:

`;
      if (reasons) {
        msg += reasons + "\n\n";
      } else {
        msg += "[Пожалуйста, укажите причины отклонения]\n\n";
      }

      msg += `После внесения изменений вы сможете повторно отправить вебинар на модерацию.

С уважением,
Команда модерации`;

      return msg;
    },

    uk: (name: string, reasons?: string) => {
      let msg = `Шановний(а) ${name},

Дякуємо за подання вебінару «${currentWebinar.title}» на перевірку.

На жаль, ми не можемо схвалити цей вебінар у поточному вигляді. Будь ласка, ознайомтеся з зауваженнями нижче та внесіть необхідні зміни перед повторним поданням:

`;
      if (reasons) {
        msg += reasons + "\n\n";
      } else {
        msg += "[Будь ласка, вкажіть причини відхилення]\n\n";
      }

      msg += `Після внесення змін ви зможете повторно подати вебінар на перевірку.

З повагою,
Команда модерації`;

      return msg;
    },
  };

  if (isLoader) {
    return (
      <div className={s.loaderContainer}>
        <Loader size={80} />
        <p className={s.loadingText}>Loading webinar review queue...</p>
      </div>
    );
  }

  if (reviewQueue.length === 0) {
    return (
      <div className={s.emptyContainer}>
        <div className={s.emptyState}>
          <CheckCircle className={s.emptyIcon} size={64} />
          <h2 className={s.emptyTitle}>All webinars reviewed! 🎉</h2>
          <p className={s.emptyText}>
            There are no webinars waiting for review at the moment.
          </p>
          {stats.reviewedToday > 0 && (
            <div className={s.statsCard}>
              <div className={s.sessionStats}>
                <div className={s.statItem}>
                  <span className={s.statLabel}>Reviewed Today</span>
                  <span className={s.statValue}>{stats.reviewedToday}</span>
                </div>
                {stats.averageReviewTime > 0 && (
                  <div className={s.statItem}>
                    <span className={s.statLabel}>Avg Review Time</span>
                    <span className={s.statValue}>
                      {formatTime(stats.averageReviewTime)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          <Button onClick={loadReviewQueue} className="mt-4">
            Refresh Queue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.mainLayout}>
      {/* Header */}
      <div className={s.header}>
        <div className={s.headerContent}>
          <div className={s.titleBlock}>
            <h1 className={s.title}>Webinar Review</h1>
            <Badge variant="destructive" className={s.pendingBadge}>
              {stats.totalPending} Pending
            </Badge>
          </div>
          <Button variant="outline" onClick={loadReviewQueue} size="sm">
            Refresh
          </Button>
        </div>

        {/* Progress Bar */}
        <div className={s.progressContainer}>
          <div className={s.progressBar}>
            <div
              className={s.progressFill}
              style={{
                width: `${(currentIndex / reviewQueue.length) * 100}%`,
              }}
            />
          </div>
          <p className={s.progressText}>
            {stats.queuePosition} / {reviewQueue.length}
          </p>
        </div>
      </div>

      {/* Content Area - List + Details */}
      <div className={s.contentArea}>
        {/* Left: Queue List */}
        <div className={s.listPanel}>
          <div className={s.listHeader}>
            <h2 className={s.listTitle}>Queue ({reviewQueue.length})</h2>
          </div>
          <div className={s.webinarList}>
            {reviewQueue.map((webinar, idx) => (
              <div
                key={webinar._id}
                className={`${s.listItem} ${idx === currentIndex ? s.active : ""}`}
                onClick={() => setCurrentIndex(idx)}
              >
                <div className={s.listItemNumber}>{idx + 1}</div>
                <div className={s.listItemContent}>
                  <div className={s.listItemTitle}>{webinar.title}</div>
                  <div className={s.listItemMeta}>
                    {formatDate(webinar.dateAndTime.date)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* List Navigation */}
          <div className={s.listNavigation}>
            <Button
              variant="outline"
              size="sm"
              onClick={moveToPrevious}
              disabled={currentIndex === 0}
              className={s.listNavBtn}
            >
              ← Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={moveToNext}
              className={s.listNavBtn}
            >
              Skip →
            </Button>
          </div>
        </div>

        {/* Right: Details Panel */}
        {currentWebinar && (
          <div className={s.detailsPanel}>
            {/* Header */}
            <div className={s.detailsHeader}>
              {avatars[currentWebinar._id] ? (
                <img
                  src={avatars[currentWebinar._id]}
                  alt={currentWebinar.title}
                  className={s.detailsAvatar}
                />
              ) : (
                <div className={s.detailsAvatarPlaceholder}>
                  <MapPin size={32} />
                </div>
              )}
              <div className={s.detailsHeaderContent}>
                <h2 className={s.detailsTitle}>{currentWebinar.title}</h2>
                <p className={s.detailsBy}>
                  <User size={14} />
                  {getSpecialistName()}
                </p>
                {currentWebinar.educationalHub && (
                  <p className={s.detailsBy}>
                    <SchoolIcon style={{ width: "14px", height: "14px" }} />
                    {getTranslation(currentWebinar.educationalHub.title, "en")}
                  </p>
                )}
              </div>
            </div>

            {/* Details Scroll Area */}
            <div className={s.detailsScroll}>
              {/* Basic Info Grid */}
              <div className={s.infoGrid}>
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Date & Time</span>
                  <span className={s.infoValue}>
                    {formatDate(currentWebinar.dateAndTime.date)}
                  </span>
                </div>
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Duration</span>
                  <span className={s.infoValue}>
                    {currentWebinar.dateAndTime.duration} min
                  </span>
                </div>
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Language</span>
                  <span className={s.infoValue}>
                    {ISO639Lang[currentWebinar.language]}
                  </span>
                </div>
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Format</span>
                  <span className={s.infoValue}>
                    {currentWebinar.format || "—"}
                  </span>
                </div>
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Participant Price</span>
                  <span className={s.infoValue}>
                    {currentWebinar.participationCost / 100} USD
                  </span>
                </div>
                {currentWebinar.recordAvailablePublic && (
                  <div className={s.infoItem}>
                    <span className={s.infoLabel}>Record price</span>
                    <span className={s.infoValue}>
                      {(currentWebinar.recordAccessCost || 0) / 100} USD
                    </span>
                  </div>
                )}
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Participants Amount</span>
                  <span className={s.infoValue}>
                    {currentWebinar.participantsAmount}
                  </span>
                </div>
              </div>

              {/* Descriptions */}
              {currentWebinar.briefDescription && (
                <div className={s.contentSection}>
                  <h3 className={s.sectionTitle}>Brief Description</h3>
                  <p className={s.sectionText}>
                    {currentWebinar.briefDescription}
                  </p>
                </div>
              )}

              {currentWebinar.description && (
                <div className={s.contentSection}>
                  <h3 className={s.sectionTitle}>Description</h3>
                  <p className={s.sectionText}>{currentWebinar.description}</p>
                </div>
              )}

              {/* Target Audience & Benefits */}
              {currentWebinar.targetAudienceText &&
                currentWebinar.targetAudienceText.length > 0 && (
                  <div className={s.contentSection}>
                    <h3 className={s.sectionTitle}>Target Audience</h3>
                    <ul className={s.bulletList}>
                      {currentWebinar.targetAudienceText.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

              {currentWebinar.participantBenefitsText &&
                currentWebinar.participantBenefitsText.length > 0 && (
                  <div className={s.contentSection}>
                    <h3 className={s.sectionTitle}>Participant Benefits</h3>
                    <ul className={s.bulletList}>
                      {currentWebinar.participantBenefitsText.map(
                        (item, idx) => (
                          <li key={idx}>{item}</li>
                        ),
                      )}
                    </ul>
                  </div>
                )}

              {/* Agenda Section */}
              {currentWebinar.agenda && currentWebinar.agenda.length > 0 && (
                <div className={s.contentSection}>
                  <h3 className={s.sectionTitle}>Agenda</h3>
                  <div className={s.agendaList}>
                    {currentWebinar.agenda.map(
                      (item: TypeWebinarAgenda, idx: number) => (
                        <div key={idx} className={s.agendaItem}>
                          <div className={s.agendaItemContent}>
                            <div className={s.agendaTitle}>{item.title}</div>
                            {item.duration && (
                              <div className={s.agendaDuration}>
                                Duration: {item.duration} min
                              </div>
                            )}
                          </div>
                          <div className={s.agendaMeta}>
                            {item.blockParticipantsMicrophones && (
                              <span className={s.agendaBadge}>
                                Mics blocked
                              </span>
                            )}
                            {item.doRecord && (
                              <span className={s.agendaBadge}>Will record</span>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Files Section with Preview */}
              <div className={s.contentSection}>
                <h3 className={s.sectionTitle}>Materials & Files</h3>
                {isLoadingFiles ? (
                  <div className={s.filesLoading}>
                    <Loader size={24} />
                    <p>Loading files...</p>
                  </div>
                ) : webinarFiles.length > 0 ? (
                  <div className={s.filesGrid}>
                    {webinarFiles.map((file, idx) => (
                      <div
                        key={idx}
                        className={s.fileCard}
                        onClick={() => handleFileClick(file)}
                      >
                        <div className={s.fileCardIcon}>
                          {file.type === "video" ? (
                            <Play size={24} />
                          ) : file.type === "pdf" ? (
                            <FileText size={24} />
                          ) : (
                            <FileText size={24} />
                          )}
                        </div>
                        <div className={s.fileCardContent}>
                          <div className={s.fileCardName}>{file.name}</div>
                          <div className={s.fileCardType}>{file.type}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={s.filesEmpty}>No materials uploaded yet</p>
                )}
              </div>

              {/* Additional Details */}
              <div className={s.contentSection}>
                <h3 className={s.sectionTitle}>Settings</h3>
                <div className={s.settingsList}>
                  <div className={s.settingItem}>
                    <span className={s.settingLabel}>
                      Participants can use camera
                    </span>
                    <span className={s.settingValue}>
                      {currentWebinar.participantsVideoSwitch ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className={s.settingItem}>
                    <span className={s.settingLabel}>Issue certificate</span>
                    <span className={s.settingValue}>
                      {currentWebinar.issuaCertificate ? "Yes" : "No"}
                    </span>
                  </div>
                  {currentWebinar.thirdPartyService?.switch && (
                    <div className={s.settingItem}>
                      <span className={s.settingLabel}>External platform</span>
                      <span className={s.settingValue}>
                        {currentWebinar.thirdPartyService.link}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {(currentWebinar.admin || currentWebinar.coSpeakerUser) && (
                <div className={s.contentSection}>
                  <h3 className={s.sectionTitle}>Webinar team</h3>
                  {currentWebinar.coSpeakerUser && (
                    <div style={{ marginBottom: "20px" }}>
                      <p>Co-speaker</p>
                      <WebinarTeam
                        searchType="coSpeakerUser"
                        userId={currentWebinar.coSpeakerUser.shortId}
                      />
                    </div>
                  )}
                  {currentWebinar.admin && (
                    <div>
                      <p>Admin</p>
                      <WebinarTeam
                        searchType="admin"
                        userId={currentWebinar.admin.shortId}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className={s.detailsActions}>
              <Button
                variant="destructive"
                onClick={handleRejectClick}
                className="flex-1"
              >
                Reject
              </Button>
              <Button
                onClick={handleApproveClick}
                className="flex-1"
                variant={"success"}
              >
                Approve
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ModalWindow
        isOpen={isConfirmationModalOpen}
        setIsOpen={setIsConfirmationModalOpen}
        title={
          confirmationAction === "approve"
            ? "Approve Webinar?"
            : "Reject Webinar?"
        }
        width="fit-content"
      >
        <div className={s.confirmationContent}>
          <p>
            You are about to{" "}
            <strong>
              {confirmationAction === "approve" ? "approve" : "reject"}
            </strong>{" "}
            this webinar. The specialist will be notified with a message.
          </p>
          <div className={s.confirmationActions}>
            <Button
              variant="outline"
              onClick={() => setIsConfirmationModalOpen(false)}
              className={s.cancelBtn}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setIsConfirmationModalOpen(false);
                if (confirmationAction === "approve") {
                  setIsApprovalMessageModalOpen(true);
                } else {
                  setIsRejectionMessageModalOpen(true);
                }
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      </ModalWindow>

      {/* Approval Message Modal */}
      <ModalWindow
        isOpen={isApprovalMessageModalOpen}
        setIsOpen={setIsApprovalMessageModalOpen}
        title="Send Approval Message"
        width="fit-content"
      >
        {currentWebinar && (
          <ReviewMessageModal
            isApproval={true}
            specialistName={getSpecialistName()}
            onSend={handleApproveWithMessage}
            onCancel={() => setIsApprovalMessageModalOpen(false)}
            userSelectedLang={(currentWebinar.language as any) || "en"}
            approvalTemplatesProps={webinarApprovalTemplates}
          />
        )}
      </ModalWindow>

      {/* Rejection Message Modal */}
      <ModalWindow
        isOpen={isRejectionMessageModalOpen}
        setIsOpen={setIsRejectionMessageModalOpen}
        title="Send Rejection Message"
        width="fit-content"
      >
        {currentWebinar && (
          <ReviewMessageModal
            isApproval={false}
            specialistName={getSpecialistName()}
            onSend={handleRejectWithMessage}
            onCancel={() => setIsRejectionMessageModalOpen(false)}
            userSelectedLang={(currentWebinar.language as any) || "en"}
            rejectionTemplatesProps={webinarRejectionTemplates}
          />
        )}
      </ModalWindow>

      {/* File Preview Modal */}
      {filePreview.file && (
        <div className={s.filePreviewOverlay} onClick={closeFilePreview}>
          <div
            className={s.filePreviewModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={s.filePreviewHeader}>
              <h3 className={s.filePreviewTitle}>{filePreview.file.name}</h3>
              <button
                className={s.filePreviewClose}
                onClick={closeFilePreview}
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            <div className={s.filePreviewContent}>
              {filePreview.file.type === "image" ? (
                <img
                  src={filePreview.file.url}
                  alt={filePreview.file.name}
                  className={s.previewImage}
                />
              ) : filePreview.file.type === "video" ? (
                <video
                  controls
                  className={s.previewVideo}
                  src={filePreview.file.url}
                >
                  Your browser does not support the video tag.
                </video>
              ) : filePreview.file.type === "pdf" ? (
                <div className={s.pdfPreviewPlaceholder}>
                  <FileText size={64} />
                  <p>PDF File: {filePreview.file.name}</p>
                  <a
                    href={filePreview.file.url}
                    download
                    className={s.downloadButton}
                  >
                    <Download size={16} />
                    Download PDF
                  </a>
                </div>
              ) : (
                <div className={s.filePreviewPlaceholder}>
                  <FileText size={64} />
                  <p>{filePreview.file.name}</p>
                  <a
                    href={filePreview.file.url}
                    download
                    className={s.downloadButton}
                  >
                    <Download size={16} />
                    Download
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Webinars;
